import { Link } from '@tanstack/react-router';
import {
  BookOpen,
  ChevronRight,
  ClipboardCheck,
  Flame,
  GraduationCap,
  Layers,
  Loader2,
  Radio,
  ShoppingBag,
  Stethoscope,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import {
  activityKindLabel,
  activityLink,
  activityStatus,
  activitySummary,
  useAdminUserActivity,
  type ActivityStatusTone,
  type AdminActivityItem,
} from '@/lib/admin/user-activity';

const KIND_ICONS: Record<string, LucideIcon> = {
  exercise: BookOpen,
  warmup: Flame,
  automatismes: Zap,
  diagnostic: Stethoscope,
  pack_test: Layers,
  live: Radio,
  evaluation: GraduationCap,
  purchase: ShoppingBag,
};

const STATUS_TONES: Record<ActivityStatusTone, string> = {
  progress: 'bg-primary-50 text-primary-700 ocean:bg-primary-500/15 ocean:text-primary-200',
  done: 'bg-success-50 text-success-800',
  muted: 'bg-ink-100 text-ink-600',
};

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

/**
 * Fiche d'un compte (admin) : liste de ses activités, la plus récente en tête,
 * avec « Voir plus » (RPC `admin_get_user_activity`, migration 0504).
 */
export function UserActivitySection({ userId }: { userId: string }) {
  const activity = useAdminUserActivity(userId);
  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section className="space-y-3">
      <h2 className="font-display text-ink-900 flex items-center gap-2 text-base font-bold">
        <ClipboardCheck className="h-4 w-4" aria-hidden /> Activités
      </h2>
      {activity.isLoading ? (
        <div className="bg-ink-100 h-24 animate-pulse rounded-2xl" />
      ) : activity.error ? (
        <p role="alert" className="text-error-600 text-sm">
          Impossible de charger les activités de ce compte.
        </p>
      ) : items.length === 0 ? (
        <p className="text-ink-500 text-sm">Aucune activité pour l’instant.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <ActivityRow key={item.key} item={item} />
          ))}
        </ul>
      )}
      {activity.hasNextPage && (
        <button
          type="button"
          onClick={() => void activity.fetchNextPage()}
          disabled={activity.isFetchingNextPage}
          className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold active:scale-[0.98] disabled:opacity-60"
        >
          {activity.isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Voir plus
        </button>
      )}
    </section>
  );
}

function ActivityRow({ item }: { item: AdminActivityItem }) {
  const Icon = KIND_ICONS[item.kind] ?? ClipboardCheck;
  const status = activityStatus(item);
  const summary = activitySummary(item);
  const link = activityLink(item);

  const body = (
    <>
      <span className="bg-ink-50 ocean:bg-white/5 text-ink-600 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink-500 block text-xs font-semibold">
          {activityKindLabel(item.kind)}
        </span>
        <span className="text-ink-900 block truncate text-sm font-semibold">
          <MathText>{item.title}</MathText>
        </span>
        <span className="text-ink-500 mt-0.5 block text-xs">
          {formatWhen(item.at)}
          {summary ? ` · ${summary}` : ''}
        </span>
      </span>
      {status && (
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
            STATUS_TONES[status.tone],
          )}
        >
          {status.label}
        </span>
      )}
      {link && <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden />}
    </>
  );

  const cls = 'border-ink-200 flex min-h-[2.75rem] items-center gap-3 rounded-xl border p-3';
  return (
    <li>
      {link ? (
        <Link {...link} className={cn(cls, 'hover:bg-ink-50 active:scale-[0.99]')}>
          {body}
        </Link>
      ) : (
        <div className={cls}>{body}</div>
      )}
    </li>
  );
}
